<template>
  <div class="component-roomtypes">
    <b-modal
      id="userModal"
      ref="userModal"
      no-fade
      centered
      static
      size="lg"
      modal-class="form-modal"
      :ok-title="$t(`buttons.${user.id != null ? 'update' : 'save'}`)"
      ok-variant="primary"
      :cancel-title="$t('buttons.cancel')"
      cancel-variant="outline-primary"
      :ok-disabled="updatePending || !$refs.userForm || !formValid || !$refs.userForm.flags.valid"
      :cancel-disabled="updatePending"
      :no-close-on-esc="updatePending"
      :no-close-on-backdrop="updatePending"
      :hide-header-close="updatePending"
      @show="modalScroll"
      @hidden="modalScroll"
      @ok.prevent="processForm"
    >
      <template #modal-header-close>
        <icon width="20" height="20" class="d-none d-md-block" type="times"/>
        <icon width="10" height="18" class="d-md-none" type="arrow-left"/>
      </template>
      <template #modal-title>
        {{ modalTitle }}
      </template>
      <ValidationObserver ref="userForm" slim>

        <div class="edge edge-amount" >
          <h3 :content="$t('pages.users.modal.role')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="select" id="role" name="role" class="mb-0" no-icon no-tooltip
                v-model="user.role_key"
                :disabled="updatePending"
                ref="role"
                :rules="rulesFor('name')"
                :options="roles"
                :placeholder="$t('pages.users.modal.add-role')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount" v-if="user.role_key && user.role_key != adminRoleId">
          <h3 :content="$t('pages.users.modal.projects')"></h3>
          <div class="row row-edit projects-area">
            <div class="col">
              <div
                class="col-xl-3 col-md-6 col-sm-12"
                v-for="{ id, name } in projects"
                :key="`project-${id}`"
              >
                <check-box :disabled="pendingProjects" v-model="user.projects" :val="id">
                  <span class="facility-item" data-toggle="tooltip" :title="name">{{ name }}</span>
                </check-box>
              </div>
            </div>
          </div>
        </div>
        <div class="edge edge-amount" v-if="user.role_key == translatorRoleId">
          <h3 :content="$t('pages.users.modal.language')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="select" id="language" name="language" class="mb-0" no-icon no-tooltip
                v-model="user.language"
                :disabled="pendingLanguages"
                ref="language"
                :rules="rulesFor('name')"
                :options="languages"
                :placeholder="$t('pages.users.modal.add-language')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.users.modal.first-name')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="key" name="key" class="mb-0" no-icon no-tooltip
                v-model="user.first_name"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('key')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.users.modal.last-name')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="key" name="key" class="mb-0" no-icon no-tooltip
                v-model="user.last_name"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('lastName')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.users.modal.email')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="key" name="key" class="mb-0" no-icon no-tooltip
                v-model="user.email"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('email')"
              />
            </div>
          </div>
        </div>
      </ValidationObserver>
    </b-modal>

    <div class="panel position-relative panel-content">
      <Breadcrumb :items=breadCrumbs() v-if="loaded"/>
      <div class="list d-none d-md-block left-edge">
        <p class="head-line justify-content-between">
          <span>{{ $t('pages.users.title') }}<spinner v-if="!loaded" /></span>
          <search-filter v-model="filter" :disabled="!loaded || pending" :placeholder="$t('pages.users.filter-tip')" />
        </p>
        <div class="plans-table" v-if="loaded">
          <table class="w-100">
            <thead>
              <tr>
                <th class="w-id">{{ $t('id') }}</th>
                <th class="w-id">{{ $t('pages.users.headers.first-name') }}</th>
                <th class="w-name">{{ $t('pages.users.headers.last-name') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.users.headers.email') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.users.headers.role') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.users.headers.language') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.users.headers.projects') }}</th>
                <th class="w-actions">{{ $t('actions') }}</th>
              </tr>
            </thead>

            <tbody v-if="filter && !filteredUsers.length">
              <tr>
                <td colspan="7" class="w-empty">{{ $t('pages.users.no-users') }}</td>
              </tr>
            </tbody>
            <tbody v-for="row in filteredUsers" :key="row.id">
              <tr class="separator before"></tr>
              <tr>
                <td>
                  {{ row.id }}
                </td>
                <td>
                  {{ row.first_name }}
                </td>
                <td>
                  <p>{{ row.last_name }}</p>
                </td>
                <td class="text-center">
                  {{ row.email }}
                </td>
                <td class="text-center">
                  {{ row.role_description }}
                </td>
                <td class="text-center">
                  <span v-if="row.language">
                    {{row.language.name}} [{{row.language.iso}}]
                  </span>
                </td>
                <td class="text-center">
                  <span v-if="row.projects_names">
                    {{ row.projects_names.join(', ') }}
                  </span>
                </td>
                <td class="actions">
                  <b-btn class="btn-icon btn-tiny" @click="editUser(row)" :disabled="updatePending">
                    <icon width="17" height="17" type="edit"
                          v-b-tooltip:page-channels.bottom="$t('pages.users.tooltips.update-user')"
                    />
                  </b-btn>
                  <b-btn class="btn-icon btn-tiny" @click="removeUser(row.id)">
                    <icon width="19" height="19" type="delete"
                          v-b-tooltip:page-channels.bottom="$t('pages.users.tooltips.remove-user')"
                    />
                  </b-btn>
                </td>
              </tr>
              <tr class="separator after"></tr>
            </tbody>
          </table>
        </div>
        <div v-if="loaded">
          <b-btn pill variant="outline-primary" class="add-new-plan"
                 @click="openCreateForm">
            <icon width="10" height="11" type="plus"/>
            {{ $t('pages.users.button-add') }}
          </b-btn>
        </div>
      </div>

      <p class="head-line d-md-none"><span>{{ $t('pages.roomtypes.heading') }}</span><spinner v-if="!loaded" /></p>
      <div class="d-md-none list-item" v-if="noUsers">
        <div class="w-empty">{{ $t('pages.users.no-users') }}</div>
      </div>
    </div>
    <div class="d-md-none add-new-plan-block" v-if="loaded">
      <b-btn pill variant="outline-primary" class="add-new-plan" @click="openCreateForm">
        <icon width="10" height="10" type="plus"/>
        {{ $t('pages.users.button-add') }}
      </b-btn>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex';
  import {
    adminRoleId, developerRoleId, roles, translatorRoleId,
  } from '@/shared';

  export default {
    name: 'Translators',
    props: {
      payload: {
        validator: (v) => ['object', 'undefined'].includes(typeof v),
        default: null,
      },
    },
    data: () => ({
      user: {
        role_key: '',
        language: '',
        email: '',
        first_name: '',
        last_lame: '',
        projects: [],
      },
      roles,
      filter: '',
      adminRoleId,
      translatorRoleId,
      developerRoleId,
    }),
    async created() {
      this.resetUserModal();
      await this.fetchData(true);
      await this.fetchProjects(true);
      await this.fetchLanguages(true);
    },
    computed: {
      ...mapGetters('translators', ['loaded', 'noUsers', 'users']),
      ...mapGetters('projects', { loadedProjects: 'loaded', noProjects: 'noProjects', projects: 'projects' }),
      ...mapGetters('languages', { loadedLanguages: 'loaded', noLanguages: 'noLanguages', languages: 'languages' }),
      ...mapState('translators', ['error', 'pending', 'updatePending']),
      ...mapState('projects', { errorProjects: 'error', pendingProjects: 'pending' }),
      ...mapState('languages', { errorLanguages: 'error', pendingLanguages: 'pending' }),
      formValid() {
        return true;
      },
      modalTitle() {
        if (this.user.id == null) {
          return this.$t('pages.users.modal.title-add');
        }
        const { id, name } = this.user;
        return this.$t('pages.users.modal.title-edit', { id, name });
      },
      filteredUsers() {
        const filter = this.filter.trim().toLowerCase();
        let ret = this.users;
        if (filter) {
          ret = ret.filter(({ id, email }) => (`${id}`.includes(filter) || email.toLowerCase().includes(filter)));
        }
        return ret;
      },
    },
    methods: {
      ...mapActions('translators', ['fetchData', 'createUser', 'updateUser', 'deleteUser']),
      ...mapActions('projects', { fetchProjects: 'fetchData' }),
      ...mapActions('languages', { fetchLanguages: 'fetchData' }),

      modalScroll(ev) {
        const modal = ev.target;
        this.$nextTick(() => {
          if (modal != null) modal.scrollTop = 0;
        });
      },
      resetUserModal() {
        this.user = {
          firstName: '',
          lastName: '',
          email: '',
          role_key: '',
          language: '',
          projects: [],
        };
        if (this.$refs.userForm != null) {
          this.$refs.userForm.reset();
        }
      },
      openCreateForm() {
        this.resetUserModal();
        this.$nextTick(this.$refs.userModal.show);
      },
      editUser(user) {
        this.user = {};
        this.resetUserModal();
        this.user = {
          ...this.user,
          ...JSON.parse(JSON.stringify(user)), // deep clone without bindings
        };
        this.$nextTick(() => {
          this.$refs.userForm.reset();
          this.$refs.userModal.show();
        });
      },
      async processForm() {
        const user = { ...this.user };
        if (this.payload != null) {
          Object.keys(this.payload).forEach((k) => {
            user[k] = this.payload[k];
          });
        }
        try {
          if (user.id != null) {
            await this.updateUser(user);
          } else {
            await this.createUser(user);
          }
          this.$refs.userModal.hide();
          this.$toastr.s({
            title: user.id ? this.$t('pages.users.alert-edit') : this.$t('pages.users.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
          await this.fetchData(true);
        } catch (error) {
          this.$toastr.e(error.message, this.$t('error'));
        }
      },
      rulesFor(field) {
        const rules = {
          required: true,
        };
        switch (field) {
          case 'name':
            break;
          case 'description':
            break;
          case 'email':
            rules.email = true;
            break;
          default:
            break;
        }
        return rules;
      },
      breadCrumbs() {
        return [
          {
            text: this.$t('pages.dashboard.title'),
            to: { name: 'dashboard' },
          },
          {
            text: this.$t('pages.users.title'),
            active: true,
          },
        ];
      },
      async removeUser(userId) {
        if (window.confirm(this.$t('pages.users.confirmation-msg'))) {
          await this.deleteUser(userId);
          await this.fetchData(true);
          this.$toastr.s({
            title: this.$t('pages.users.alert-delete'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
        }
      },
    },
  };
</script>
